import { isAfter, isBefore, isDate } from 'date-fns';
import { ActivityInstance, SubtaskItem } from '@api';

/**
 * Härleder en aktivitets status på klienten — samma regler som
 * Missed/Ongoing/Upcoming-vyerna (backend skickar inte status, det härleds av
 * datum/flaggor). Centraliserat för barnvyns kort/listor.
 */
export type ChildActivityStatus = 'done' | 'ongoing' | 'missed' | 'upcoming';

export const getChildActivityStatus = (
  instance: ActivityInstance,
  now: Date = new Date(),
): ChildActivityStatus => {
  if (isDate(instance.dateIsCompleted)) return 'done';
  if (isDate(instance.dateIsStarted)) return 'ongoing';
  if (isBefore(instance.startDate, now)) return 'missed';
  if (isAfter(instance.startDate, now)) return 'upcoming';
  return 'upcoming';
};

/**
 * Semantisk roll för en status (skamfri statusmodell, Pipeline 1). Frikopplar
 * UI-tonen från statusnamnet: 'missed' blir 'attention' (mjuk bärnsten, ej
 * rött/skam), klart blir 'positive', pågående/kommande är neutralt. 'error'
 * reserveras för faktiska fel. Komponenter kan därmed välja färg via roll
 * istället för att hårdkoda mappningar per status.
 */
export type StatusRole = 'neutral' | 'positive' | 'attention' | 'error';

const STATUS_ROLE: Record<ChildActivityStatus, StatusRole> = {
  done: 'positive',
  ongoing: 'neutral',
  upcoming: 'neutral',
  missed: 'attention',
};

export const getStatusRole = (status: ChildActivityStatus): StatusRole => STATUS_ROLE[status];

/**
 * Resultatet av getNextActivity: den instans barnvyn ska lyfta som "nästa" plus
 * dess härledda status. Endast 'ongoing'/'upcoming' är relevanta här — klara och
 * missade aktiviteter filtreras bort (de ska inte lyftas som nästa steg).
 */
export type NextActivityResult = {
  instance: ActivityInstance;
  status: Extract<ChildActivityStatus, 'ongoing' | 'upcoming'>;
};

/**
 * Sorterbart starttidsvärde. En saknad/ogiltig startDate behandlas som +Infinity
 * (sist) så att en trasig instance aldrig kapar platsen som "nästa". Detta speglar
 * spec-kravet om defensiv sortering: NaN/odefinierat ska inte kunna vinna.
 */
const startTime = (instance: ActivityInstance): number => {
  const t = new Date(instance.startDate).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

/**
 * Väljer den aktivitet barnvyn ska framhäva som "nästa upp" (Pipeline 3,
 * igångsättningsstöd). Regler:
 *   - Filtrera bort done/missed (endast ongoing/upcoming är kandidater).
 *   - Finns någon 'ongoing' → den med tidigast startDate.
 *   - Annars den tidigaste 'upcoming'.
 *   - Annars null (tom dag, alla klara eller alla missade).
 * Ren funktion, defensiv mot saknad/NaN startDate — kraschar aldrig.
 */
export const getNextActivity = (
  instances: ActivityInstance[],
  now: Date = new Date(),
): NextActivityResult | null => {
  const candidates: NextActivityResult[] = [];
  instances.forEach((instance) => {
    const status = getChildActivityStatus(instance, now);
    if (status === 'ongoing' || status === 'upcoming') {
      candidates.push({ instance, status });
    }
  });
  if (candidates.length === 0) return null;

  const ongoing = candidates.filter((c) => c.status === 'ongoing');
  const pool = ongoing.length > 0 ? ongoing : candidates;

  return pool.reduce((earliest, current) =>
    startTime(current.instance) < startTime(earliest.instance) ? current : earliest,
  );
};

/**
 * Väljer nästa obockade delsteg (steg-1-stöd). Sorterar en kopia på position och
 * returnerar första delsteget vars subtaskId inte finns i checkedSubtaskIds.
 * Returnerar null när alla är bockade eller listan är tom. Ren funktion.
 */
export const getNextSubtask = (
  subtasks: SubtaskItem[],
  checkedSubtaskIds: number[] = [],
): SubtaskItem | null => {
  const sorted = [...subtasks].sort((a, b) => a.position - b.position);
  return sorted.find((s) => !checkedSubtaskIds.includes(s.subtaskId)) ?? null;
};
